/* eslint-disable react-refresh/only-export-components */
import { type PaymentRecord } from '@/hooks/payments/usePayments';
import { dateKeyInTimeZone, formatDateTimeInTimeZone } from '@/lib/date-values';
import { isClearingBankPayment } from '@/lib/payments/savedPaymentMethods';
import type { Payment } from '@/types/invoice';
import { TableStatusBlock } from '@/components/molecules/TableStatusBlock';

export type StatusFilter =
  | 'ALL'
  | 'SUCCEEDED'
  | 'PENDING'
  | 'PARTIALLY_REFUNDED'
  | 'REVERSED'
  | 'REFUNDED'
  | 'DISPUTED'
  | 'DISPUTE_UNDER_REVIEW'
  | 'CHARGEBACK_WON'
  | 'CHARGEBACK_LOST'
  | 'FAILED';

export const STATUS_FILTERS: StatusFilter[] = [
  'ALL',
  'SUCCEEDED',
  'PENDING',
  'PARTIALLY_REFUNDED',
  'REVERSED',
  'REFUNDED',
  'DISPUTED',
  'DISPUTE_UNDER_REVIEW',
  'CHARGEBACK_WON',
  'CHARGEBACK_LOST',
  'FAILED',
];

export const STATUS_DOT_COLORS: Record<string, string> = {
  COMPLETED: 'bg-green-500',
  SUCCEEDED: 'bg-green-500',
  PENDING: 'bg-yellow-500',
  PARTIALLY_REFUNDED: 'bg-amber-500',
  REVERSED: 'bg-slate-500',
  REFUNDED: 'bg-red-500',
  DISPUTED: 'bg-orange-500',
  DISPUTE_UNDER_REVIEW: 'bg-blue-500',
  CHARGEBACK_WON: 'bg-emerald-500',
  CHARGEBACK_LOST: 'bg-red-600',
  FAILED: 'bg-red-500',
};

const STATUS_STYLE: Record<string, string> = {
  COMPLETED: 'bg-green-100 text-green-800',
  SUCCEEDED: 'bg-green-100 text-green-800',
  PENDING: 'bg-yellow-100 text-yellow-800',
  PARTIALLY_REFUNDED: 'bg-amber-100 text-amber-800',
  REVERSED: 'bg-slate-100 text-slate-700',
  REFUNDED: 'bg-red-100 text-red-700',
  DISPUTED: 'bg-orange-100 text-orange-800',
  DISPUTE_UNDER_REVIEW: 'bg-blue-100 text-blue-800',
  CHARGEBACK_WON: 'bg-emerald-100 text-emerald-800',
  CHARGEBACK_LOST: 'bg-red-100 text-red-700',
  FAILED: 'bg-red-100 text-red-700',
};

export function formatPaymentAmount(n: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

export function paymentDate(payment: PaymentRecord) {
  return payment.processedAt ?? payment.createdAt;
}

/**
 * A payment's day in the company timezone. The list, the table (and its
 * export) and the detail panel must agree; formatting in the viewer's browser
 * timezone put a late-evening payment on a different day in the list than on
 * its own detail page.
 */
export function formatPaymentDay(
  payment: PaymentRecord,
  timeZone: string | undefined,
  style: 'display' | 'iso' = 'display',
): string {
  const raw = paymentDate(payment);
  if (!raw || Number.isNaN(new Date(raw).getTime())) return '-';
  return style === 'iso'
    ? dateKeyInTimeZone(raw, timeZone)
    : formatDateTimeInTimeZone(raw, timeZone, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
}

export function paymentInvoiceLabel(payment: PaymentRecord) {
  if (payment.invoiceId === 'QB-IMPORT') return 'QB Import';
  if (payment.invoiceNumber?.trim()) return payment.invoiceNumber;
  if (!payment.invoiceId) return payment.estimateNumber?.trim() || 'No invoice';
  return 'Invoice unavailable';
}

/**
 * The negative row `POST /payments/:id/reverse` writes when the bank takes a
 * payment back (an ACH return or a bounced check). It records money the
 * customer owes again, not money received, so it must never read as a payment
 * that succeeded, nor offer a refund or another return.
 */
export function isBankReturnOffset(payment: Pick<PaymentRecord, 'reversalOf'>) {
  return Boolean(payment.reversalOf);
}

export const BANK_RETURN_LABEL = 'Bank return';
/** Amber, the warning tone the other money-at-risk statuses use. */
export const BANK_RETURN_CLASS = 'bg-amber-100 text-amber-800';

/**
 * A bank debit the processor took, on its way to settling
 * (`isClearingBankPayment`). Keeps PENDING's colour; only the words change, so
 * staff stop reading a normal multi-day ACH as stuck.
 */
export const CLEARING_LABEL = 'Pending ACH';
export const CLEARING_HINT =
  'Bank payment accepted by the processor. Bank transfers usually take 3–5 business days to settle.';

/**
 * A bank payment staff cancelled before it cleared (Cancel bank payment). It is
 * stored FAILED, because nothing was taken, and stamped `cancelledByStaffAt`;
 * "Failed" read as the bank refusing it, when staff stopped it on purpose.
 * The contact payments list sends the stamp as a field of its own; every other
 * payment response carries it in `gatewayMetadata`.
 */
export const CANCELLED_LABEL = 'Cancelled';
export const CANCELLED_CLASS = 'bg-gray-100 text-gray-700';

/** Table-cell status for a staff-cancelled bank payment. */
export function CancelledStatusBlock() {
  return (
    <TableStatusBlock status="CANCELLED" label={CANCELLED_LABEL} className={CANCELLED_CLASS} />
  );
}

/** Table-cell status for a clearing bank debit. */
export function ClearingStatusBlock() {
  return (
    <span title={CLEARING_HINT}>
      <TableStatusBlock status="PENDING" label={CLEARING_LABEL} />
    </span>
  );
}

export type RefundOption = 'CASH_REFUND' | 'CUSTOMER_CREDIT';

/**
 * Every way this payment can be refunded by the current user, in display
 * order. One source for both the detail pane's Refund button (shown when this
 * is non-empty) and the options its dialog lists, so the two cannot disagree.
 *
 * Cash refund and customer credit need refundable capacity plus their own
 * permission. A bank return is not a refund and is not listed here — see
 * {@link paymentReturnAction}.
 */
export function paymentRefundOptions(
  payment: PaymentRecord,
  permissions: {
    canCashRefund: boolean;
    canIssueCustomerCredit: boolean;
  },
): RefundOption[] {
  if (payment.amount === undefined || isBankReturnOffset(payment)) return [];
  const status = payment.status.toUpperCase();
  const remainingRefundable =
    payment.refundableBalance ?? Math.max(0, payment.amount - (payment.refundedAmount ?? 0));
  const isRefundable =
    (status === 'COMPLETED' || status === 'SUCCEEDED' || status === 'PARTIALLY_REFUNDED') &&
    remainingRefundable > 0;

  const options: RefundOption[] = [];
  if (isRefundable && permissions.canCashRefund) options.push('CASH_REFUND');
  if (isRefundable && permissions.canIssueCustomerCredit) options.push('CUSTOMER_CREDIT');
  return options;
}

export type PaymentReturnKind = 'ach_return' | 'check_bounce';

export interface PaymentReturnAction {
  kind: PaymentReturnKind;
  label: string;
  enabled: boolean;
  /** Why the return cannot be recorded; shown under the disabled menu item. */
  blockedReason: string | null;
}

/**
 * The "the bank took this payment back" action, driven entirely by the
 * server's `reversal` block — the server knows whether the payment is a
 * processor payment, already refunded, or itself an offset. Null when there is
 * nothing to show: not a returnable kind, an offset row, no `payments:edit`
 * (what `POST /payments/:id/reverse` requires), or ineligible with no reason
 * to explain it.
 */
export function paymentReturnAction(
  payment: PaymentRecord,
  canRecordReturn: boolean,
): PaymentReturnAction | null {
  const reversal = payment.reversal;
  if (!reversal?.kind || isBankReturnOffset(payment) || !canRecordReturn) return null;
  // A scanned check is an ACH debit Payabli settles and reports the return of
  // itself: neither "returned by bank" nor "bounced" is a thing staff record.
  if (isScannedCheck(payment.flow)) return null;
  const label =
    reversal.kind === 'check_bounce' ? 'Mark check as bounced' : 'Mark as returned by bank';
  if (reversal.eligible) {
    return { kind: reversal.kind, label, enabled: true, blockedReason: null };
  }
  const blockedReason = reversal.blockedReason?.trim();
  return blockedReason ? { kind: reversal.kind, label, enabled: false, blockedReason } : null;
}

/** `Payment.flow` of a check scanned and debited through Payabli. */
export const CHEQUE_SCAN_FLOW = 'CHEQUE_SCAN';

export const SCANNED_CHECK_LABEL = 'Check (scanned)';

/** A scanned check is recorded as the ACH debit it becomes; `flow` tells. */
export function isScannedCheck(flow: string | null | undefined): boolean {
  return flow === CHEQUE_SCAN_FLOW;
}

/**
 * Methods that are initials, not words: they stay upper case in every casing,
 * or a bank payment reads "Ach" — and a `capitalize` line cannot fix it,
 * since that only touches the first letter.
 */
const ACRONYM_PAYMENT_METHODS = new Set(['ACH']);

/** "CREDIT_CARD" → "Credit Card"; "ACH" → "ACH"; "-" for none. */
export function titleCasePaymentMethod(method: string | null | undefined): string {
  if (!method) return '-';
  if (ACRONYM_PAYMENT_METHODS.has(method.toUpperCase())) return method.toUpperCase();
  return method
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/**
 * For a line the page renders `capitalize`: "CREDIT_CARD" → "credit card",
 * which the CSS title-cases, while "ACH" is kept as it is.
 */
export function lowerCasePaymentMethod(method: string | null | undefined): string {
  if (!method) return '';
  if (ACRONYM_PAYMENT_METHODS.has(method.toUpperCase())) return method.toUpperCase();
  return method.toLowerCase().replaceAll('_', ' ');
}

const HUMAN_PAYMENT_METHOD_LABELS: Record<string, string> = {
  CARD: 'Card',
  CARD_PRESENT: 'Card',
  CREDIT_CARD: 'Credit card',
  DEBIT_CARD: 'Debit card',
  ACH: 'Bank account (ACH)',
  BANK_ACCOUNT: 'Bank account (ACH)',
  CASH: 'Cash',
  CHECK: 'Check',
  FINANCING: 'Financing',
  OTHER: 'Other',
};

/**
 * A method in words, for a line that is not styled `capitalize`:
 * "DEBIT_CARD" → "Debit card", "ACH" → "Bank account (ACH)", "—" for none.
 * A method this build does not know reads as words, never its raw code.
 */
export function humanPaymentMethod(method: string | null | undefined): string {
  if (!method) return '—';
  const known = HUMAN_PAYMENT_METHOD_LABELS[method.toUpperCase()];
  if (known) return known;
  const words = method.toLowerCase().replaceAll('_', ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : '—';
}

/** The processor's own name, or null when the payment does not say which one. */
export function processorName(gatewayProvider?: string | null): string | null {
  switch (gatewayProvider?.toLowerCase()) {
    case 'stripe':
      return 'Stripe';
    case 'payabli':
      return 'Payabli';
    default:
      return null;
  }
}

/** A bare processor result code, e.g. Payabli's "D0305". */
const BARE_PROCESSOR_CODE = /^[A-Z]{1,3}\d{2,6}$/;

/** Codes whose meaning staff need spelled out; any other code is just named. */
const PROCESSOR_CODE_MEANINGS: Record<string, string> = {
  D0305: 'refused this for now; it is usually temporary, so try again in a few minutes',
};

/**
 * A failure reason as staff should read it. The server stores a processor's
 * decline CODE when it has one, so a failed charge can carry just "D0305";
 * shown bare, that reads like a typo. Name whose code it is instead
 * ("Payabli code D0305"). Any other reason is already words and is left alone.
 */
export function processorFailureText(
  reason: string | null | undefined,
  gatewayProvider?: string | null,
): string {
  const text = reason?.trim() ?? '';
  if (!BARE_PROCESSOR_CODE.test(text)) return text;
  const processor = processorName(gatewayProvider) ?? 'The processor';
  const meaning = PROCESSOR_CODE_MEANINGS[text.toUpperCase()];
  return meaning
    ? `${processor} ${meaning} (code ${text}).`
    : `${processorName(gatewayProvider) ?? 'Processor'} code ${text}`;
}

/**
 * How a payment's method reads on a staff screen. A scanned check is stored as
 * `paymentMethod: ACH` with flow CHEQUE_SCAN — money that came in as a check —
 * so it reads "Check (scanned)". Every other payment keeps the formatting the
 * screen already uses (`format`, Title Case by default).
 *
 * Only when the method itself is there: a role the server masks payment info
 * from gets no method, and `flow` must not tell it what the method was.
 */
export function paymentMethodLabel(
  method: string | null | undefined,
  flow: string | null | undefined,
  format: (method: string) => string = titleCasePaymentMethod,
): string {
  if (method && isScannedCheck(flow)) return SCANNED_CHECK_LABEL;
  return format(method ?? '');
}

/**
 * `paymentMethodLabel` for a line styled `capitalize`, which would otherwise
 * print "Check (Scanned)".
 */
export function PaymentMethodText({
  method,
  flow,
  format,
}: {
  method: string | null | undefined;
  flow: string | null | undefined;
  format?: (method: string) => string;
}) {
  const label = paymentMethodLabel(method, flow, format);
  return label === SCANNED_CHECK_LABEL ? (
    <span className="normal-case">{label}</span>
  ) : (
    <>{label}</>
  );
}

export function paymentStatusLabel(status: string) {
  return status
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/**
 * An invoice's payment-history row status, in the words the payments screens
 * use: a bank debit the processor accepted is "Pending ACH", not a bare
 * "Pending" that a card still being verified also reads.
 *
 * Only when the row shows its method (`methodVisible`). A PAYMENT_INFO role has
 * the method stripped, and a Stripe debit is recognisable without it, so
 * "Pending ACH" would name the rail the mask hides — the job Payments section's
 * rule (`paymentRowStatus`).
 */
export function invoicePaymentStatusLabel(
  payment: Pick<Payment, 'status' | 'method' | 'gatewayProvider' | 'gatewayTxnId' | 'updatedBy'>,
  methodVisible: boolean,
): string {
  if (methodVisible && isClearingBankPayment({ ...payment, paymentMethod: payment.method })) {
    return CLEARING_LABEL;
  }
  return paymentStatusLabel(payment.status ?? 'RECORDED');
}

export function PaymentStatusBadge({
  status,
  bankReturn = false,
  clearing = false,
  cancelled = false,
}: {
  status: string;
  /** A bank-return offset row: labelled as the return, not its SUCCEEDED status. */
  bankReturn?: boolean;
  /** A bank debit on its way to settling (`isClearingBankPayment`). */
  clearing?: boolean;
  /** A bank payment staff cancelled before it cleared (`isStaffCancelledPayment`). */
  cancelled?: boolean;
}) {
  const cls = bankReturn
    ? BANK_RETURN_CLASS
    : cancelled
      ? CANCELLED_CLASS
      : (STATUS_STYLE[status.toUpperCase()] ?? 'bg-gray-100 text-gray-700');
  return (
    <span
      // `capitalize` only for status labels: it would render "Bank Return" and
      // disagree with every other surface's "Bank return".
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${bankReturn ? '' : 'capitalize'} ${cls}`}
      title={!bankReturn && clearing ? CLEARING_HINT : undefined}
    >
      {bankReturn
        ? BANK_RETURN_LABEL
        : cancelled
          ? CANCELLED_LABEL
          : clearing
            ? CLEARING_LABEL
            : paymentStatusLabel(status)}
    </span>
  );
}

/**
 * The status filter queries the stored status, and a bank debit still clearing
 * is stored PENDING while the row reads "Pending ACH" — so the Pending option says
 * it returns both.
 */
export function statusFilterOptionLabel(filter: StatusFilter): string {
  if (filter === 'ALL') return 'All statuses';
  if (filter === 'PENDING') return `${paymentStatusLabel(filter)} or ${CLEARING_LABEL}`;
  return paymentStatusLabel(filter);
}
